import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { LayoutDashboard, TrendingUp, TrendingDown, AlertTriangle, Activity, Target, Users, Filter, RefreshCw, ClipboardList, ArrowDownRight, Building2 } from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, BarChart, Bar, ComposedChart, ReferenceLine,
} from 'recharts';
import { toast } from 'sonner';

const fmt = (v) => (Number(v) || 0).toLocaleString('it-IT', { maximumFractionDigits: 2 });
const fmtInt = (v) => (Number(v) || 0).toLocaleString('it-IT');
const fmtPct = (v) => `${Number(v || 0).toFixed(2)}%`;

const ESITO_COLORS = {
  'Conforme': '#10b981',
  'Riprendibile': '#f59e0b',
  'Da rilavorare': '#f97316',
  'Scarto': '#ef4444',
  'N/D': '#94a3b8',
};

// ───── Delta helpers ─────
function DeltaBadge({ value, unit = '%', positiveGood = true }) {
  if (value === null || value === undefined) return <span className="text-[10px] text-slate-400">—</span>;
  const n = Number(value);
  if (n === 0) return <span className="text-[10px] text-slate-500">0{unit}</span>;
  const good = (positiveGood && n > 0) || (!positiveGood && n < 0);
  const color = good ? 'text-emerald-600' : 'text-red-600';
  const Icon = n > 0 ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold ${color}`}>
      <Icon className="w-3 h-3" />{n > 0 ? '+' : ''}{n}{unit}
    </span>
  );
}

function KpiCard({ label, value, unit, sub, icon: Icon, delta, tone = 'neutral', testid }) {
  const tones = {
    good: 'border-emerald-200 bg-emerald-50/50',
    warn: 'border-amber-200 bg-amber-50/50',
    bad: 'border-red-200 bg-red-50/50',
    neutral: 'border-slate-200 bg-white',
  };
  return (
    <Card className={tones[tone]} data-testid={testid}>
      <CardContent className="p-3">
        <div className="flex items-start justify-between">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
          {Icon && <Icon className="w-4 h-4 text-slate-400" />}
        </div>
        <p className="text-2xl font-bold font-mono mt-1">{value}<span className="text-sm ml-0.5 text-slate-500">{unit}</span></p>
        <div className="flex items-center justify-between mt-1">
          {sub && <p className="text-[10px] text-slate-500">{sub}</p>}
          {delta !== undefined && <DeltaBadge {...delta} />}
        </div>
      </CardContent>
    </Card>
  );
}

// ───── Filters bar ─────
function FiltersBar({ value, setValue, filters, onRefresh, hideCustomer = false }) {
  return (
    <Card><CardContent className="p-3">
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex items-center gap-1"><Filter className="w-4 h-4 text-slate-400" /><span className="text-xs font-semibold text-slate-700">Filtri</span></div>
        <div>
          <Label className="text-[10px]">Dal</Label>
          <Input type="date" value={value.date_from} onChange={e => setValue({ ...value, date_from: e.target.value })} className="h-8 text-xs" data-testid="filter-date-from" />
        </div>
        <div>
          <Label className="text-[10px]">Al</Label>
          <Input type="date" value={value.date_to} onChange={e => setValue({ ...value, date_to: e.target.value })} className="h-8 text-xs" data-testid="filter-date-to" />
        </div>
        {!hideCustomer && (
          <div>
            <Label className="text-[10px]">Cliente</Label>
            <Select value={value.customer_code || '__all'} onValueChange={v => setValue({ ...value, customer_code: v === '__all' ? '' : v })}>
              <SelectTrigger className="h-8 text-xs w-40" data-testid="filter-customer"><SelectValue placeholder="Tutti" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">Tutti</SelectItem>
                {(filters?.customers || []).map(c => <SelectItem key={c.code} value={c.code}>{c.code} · {c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div>
          <Label className="text-[10px]">Prodotto</Label>
          <Select value={value.product_code || '__all'} onValueChange={v => setValue({ ...value, product_code: v === '__all' ? '' : v })}>
            <SelectTrigger className="h-8 text-xs w-32" data-testid="filter-product"><SelectValue placeholder="Tutti" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">Tutti</SelectItem>
              {(filters?.products || []).map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px]">Fase</Label>
          <Select value={value.fase || '__all'} onValueChange={v => setValue({ ...value, fase: v === '__all' ? '' : v })}>
            <SelectTrigger className="h-8 text-xs w-28" data-testid="filter-fase"><SelectValue placeholder="Tutte" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">Tutte</SelectItem>
              {(filters?.fasi || []).map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px]">Causale</Label>
          <Select value={value.causale || '__all'} onValueChange={v => setValue({ ...value, causale: v === '__all' ? '' : v })}>
            <SelectTrigger className="h-8 text-xs w-40" data-testid="filter-causale"><SelectValue placeholder="Tutte" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">Tutte</SelectItem>
              {(filters?.causali || []).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button size="sm" variant="outline" onClick={onRefresh} data-testid="filter-refresh"><RefreshCw className="w-3.5 h-3.5 mr-1" />Aggiorna</Button>
      </div>
    </CardContent></Card>
  );
}

// ───── TAB A · Direzionale ─────
function DirezionalePanel({ data }) {
  if (!data) return <div className="text-center text-slate-400 py-12">Caricamento…</div>;
  const { kpi, deltas } = data;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-7 gap-2">
        <KpiCard testid="dir-kpi-total" label="Controlli totali" value={fmtInt(kpi.total)} icon={Activity} tone="neutral"
          delta={{ value: deltas.total_pct, positiveGood: true }} sub={`vs periodo prec.`} />
        <KpiCard testid="dir-kpi-conformity" label="Conformità globale" value={kpi.conformity_rate} unit="%" icon={Target}
          tone={kpi.conformity_rate >= 95 ? 'good' : kpi.conformity_rate >= 90 ? 'warn' : 'bad'}
          delta={{ value: deltas.conformity_pct, positiveGood: true, unit: 'pp' }} />
        <KpiCard testid="dir-kpi-derogate" label="Accett. con deroga" value={fmtInt(kpi.derogate)} icon={Target}
          tone={kpi.derogate === 0 ? 'neutral' : 'warn'} sub={`su ${kpi.total} controlli`} />
        <KpiCard testid="dir-kpi-ppm" label="PPM difetti" value={fmtInt(kpi.ppm)} icon={AlertTriangle}
          tone={kpi.ppm < 20000 ? 'good' : kpi.ppm < 50000 ? 'warn' : 'bad'}
          delta={{ value: deltas.ppm_delta, positiveGood: false, unit: '' }} sub="parti per milione" />
        <KpiCard testid="dir-kpi-critiche" label="NC critiche" value={fmtInt(kpi.nc_critiche)} icon={AlertTriangle}
          tone={kpi.nc_critiche === 0 ? 'good' : kpi.nc_critiche <= 3 ? 'warn' : 'bad'} sub="scarti o forzature" />
        <KpiCard testid="dir-kpi-forzature" label="Forzature esito" value={kpi.forzature_rate} unit="%" icon={AlertTriangle}
          delta={{ value: deltas.forzature_delta, positiveGood: false, unit: '' }} sub={`${kpi.forzature} su ${kpi.total}`} />
        <KpiCard testid="dir-kpi-tempo" label="Tempo medio" value={kpi.tempo_medio_min} unit=" min" icon={Activity}
          sub="per controllo" tone="neutral" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2"><CardHeader className="pb-1"><CardTitle className="text-sm">Andamento conformità</CardTitle></CardHeader>
          <CardContent className="p-3">
            <ResponsiveContainer width="100%" height={240}>
              <ComposedChart data={data.timeseries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis yAxisId="left" tick={{ fontSize: 10 }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} domain={[0, 100]} unit="%" />
                <Tooltip contentStyle={{ fontSize: 11 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar yAxisId="left" dataKey="conformi" name="Conformi" fill="#10b981" stackId="a" />
                <Bar yAxisId="left" dataKey="derogate" name="Con deroga" fill="#f59e0b" stackId="a" />
                <Bar yAxisId="left" dataKey="non_conformi" name="Non conformi" fill="#ef4444" stackId="a" />
                <Line yAxisId="right" type="monotone" dataKey="conformity_rate" name="% Conformità" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card><CardHeader className="pb-1"><CardTitle className="text-sm">Distribuzione esiti</CardTitle></CardHeader>
          <CardContent className="p-3">
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={data.esiti_donut} innerRadius={45} outerRadius={80} dataKey="value" nameKey="name" label={{ fontSize: 10 }}>
                  {(data.esiti_donut || []).map((e) => <Cell key={e.name} fill={ESITO_COLORS[e.name] || '#94a3b8'} />)}
                </Pie>
                <Tooltip contentStyle={{ fontSize: 11 }} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <Card><CardHeader className="pb-1"><CardTitle className="text-sm">Heatmap Cliente × Mese (tasso NC)</CardTitle></CardHeader>
        <CardContent className="p-3">
          <HeatmapCustomerMonth heatmap={data.heatmap} />
        </CardContent>
      </Card>
    </div>
  );
}

function HeatmapCustomerMonth({ heatmap }) {
  if (!heatmap || !heatmap.customers.length) {
    return <p className="text-center text-slate-400 text-sm py-6">Nessun dato per la heatmap.</p>;
  }
  const cellMap = {};
  for (const c of heatmap.cells) cellMap[`${c.customer}|${c.month}`] = c;
  const colorFor = (rate) => {
    if (rate === undefined) return '#f8fafc';
    if (rate < 2) return '#d1fae5';
    if (rate < 5) return '#fef3c7';
    if (rate < 10) return '#fed7aa';
    return '#fecaca';
  };
  return (
    <div className="overflow-x-auto">
      <table className="text-xs border-collapse">
        <thead>
          <tr>
            <th className="text-left px-2 py-1 sticky left-0 bg-white border-b">Cliente</th>
            {heatmap.months.map(m => <th key={m} className="px-2 py-1 text-center text-[10px] font-medium text-slate-500 border-b">{m}</th>)}
          </tr>
        </thead>
        <tbody>
          {heatmap.customers.map(cust => (
            <tr key={cust}>
              <td className="px-2 py-1 font-semibold sticky left-0 bg-white border-r">{cust}</td>
              {heatmap.months.map(m => {
                const c = cellMap[`${cust}|${m}`];
                return (
                  <td key={m} className="p-0.5">
                    {c ? (
                      <div className="rounded p-1 text-center" style={{ backgroundColor: colorFor(c.nc_rate), minWidth: 60 }} title={`${c.total} controlli · ${c.nc} NC`}>
                        <div className="font-bold text-[10px]">{c.nc_rate}%</div>
                        <div className="text-[9px] text-slate-600">{c.total} ctrl</div>
                      </div>
                    ) : <div className="rounded p-1 text-center text-slate-300 text-[10px]" style={{ minWidth: 60 }}>—</div>}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex gap-2 mt-2 text-[10px] text-slate-500">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded" style={{ backgroundColor: '#d1fae5' }}></span>&lt;2%</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded" style={{ backgroundColor: '#fef3c7' }}></span>2-5%</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded" style={{ backgroundColor: '#fed7aa' }}></span>5-10%</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded" style={{ backgroundColor: '#fecaca' }}></span>&gt;10%</span>
      </div>
    </div>
  );
}

// ───── TAB B · Operativa ─────
function OperativaPanel({ data }) {
  if (!data) return <div className="text-center text-slate-400 py-12">Caricamento…</div>;
  const { today, rqp_aperte, kpi, pareto_nc, rankings, spc_lite } = data;
  return (
    <div className="space-y-4">
      {rqp_aperte.count > 0 && (
        <Card className="border-red-300 bg-red-50/50" data-testid="op-alert-rqp">
          <CardContent className="p-3 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <p className="text-sm"><strong className="text-red-900">{rqp_aperte.count} richieste RQP aperte</strong> — vai a Registro RQP per gestirle.</p>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <KpiCard testid="op-kpi-today" label="Controlli oggi" value={today.controlli_fatti} icon={ClipboardList} tone="neutral" />
        <KpiCard testid="op-kpi-today-conf" label="Conformità oggi" value={today.conformity_rate} unit="%" icon={Target}
          tone={today.conformity_rate >= 95 ? 'good' : today.conformity_rate >= 90 ? 'warn' : 'bad'} />
        <KpiCard testid="op-kpi-nc-oggi" label="NC oggi" value={today.nc_oggi} icon={AlertTriangle}
          tone={today.nc_oggi === 0 ? 'good' : 'bad'} />
        <KpiCard testid="op-kpi-rqp" label="RQP aperte" value={rqp_aperte.count} icon={ClipboardList}
          tone={rqp_aperte.count === 0 ? 'good' : 'warn'} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card><CardHeader className="pb-1"><CardTitle className="text-sm">Pareto Non Conformità</CardTitle></CardHeader>
          <CardContent className="p-3">
            {!pareto_nc.length ? <p className="text-slate-400 text-sm text-center py-6">Nessuna NC nel periodo.</p> : (
              <ResponsiveContainer width="100%" height={240}>
                <ComposedChart data={pareto_nc}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 9 }} angle={-15} textAnchor="end" height={60} />
                  <YAxis yAxisId="left" tick={{ fontSize: 10 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} domain={[0, 100]} unit="%" />
                  <Tooltip contentStyle={{ fontSize: 11 }} />
                  <Bar yAxisId="left" dataKey="count" fill="#ef4444" name="N° NC" />
                  <Line yAxisId="right" type="monotone" dataKey="cumulative_pct" stroke="#0369a1" strokeWidth={2} dot={{ r: 3 }} name="% Cumulata" />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card><CardHeader className="pb-1"><CardTitle className="text-sm">Ranking prodotti per NC</CardTitle></CardHeader>
          <CardContent className="p-3">
            <RankingTable rows={rankings.top_prodotti_by_nc} nameLabel="Prodotto" />
          </CardContent>
        </Card>

        <Card><CardHeader className="pb-1"><CardTitle className="text-sm">Top operatori per volume</CardTitle></CardHeader>
          <CardContent className="p-3">
            <RankingTable rows={rankings.top_operatori_by_volume} nameLabel="Operatore" />
          </CardContent>
        </Card>

        <Card><CardHeader className="pb-1"><CardTitle className="text-sm">Top causali per NC</CardTitle></CardHeader>
          <CardContent className="p-3">
            <RankingTable rows={rankings.top_causali_by_nc} nameLabel="Causale" />
          </CardContent>
        </Card>
      </div>

      <Card><CardHeader className="pb-1"><CardTitle className="text-sm">SPC-Lite · Deriva statistica quote critiche</CardTitle></CardHeader>
        <CardContent className="p-3">
          {!spc_lite.length ? <p className="text-slate-400 text-sm text-center py-6">Nessuna quota dimensionale con dati sufficienti (min 5 punti).</p> : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {spc_lite.map((f) => <SpcChart key={f.feature} f={f} />)}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="text-xs text-slate-500 flex items-center gap-2">
        <span>KPI periodo:</span>
        <Badge>Controlli {kpi.total}</Badge>
        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border">Conformi {kpi.conformi}</Badge>
        <Badge className="bg-amber-50 text-amber-700 border-amber-200 border" data-testid="op-badge-derogate">Con deroga {kpi.derogate}</Badge>
        <Badge className="bg-red-50 text-red-700 border-red-200 border">NC {kpi.non_conformi}</Badge>
        <Badge className="bg-amber-50 text-amber-700 border-amber-200 border">Forzature {kpi.forzature}</Badge>
      </div>
    </div>
  );
}

function RankingTable({ rows, nameLabel }) {
  if (!rows || !rows.length) return <p className="text-slate-400 text-sm text-center py-4">Nessun dato</p>;
  return (
    <table className="w-full text-xs">
      <thead><tr className="border-b text-left text-[10px] uppercase tracking-wider text-slate-500">
        <th className="py-1.5 px-2">{nameLabel}</th>
        <th className="py-1.5 px-2 text-right">Ctrl</th>
        <th className="py-1.5 px-2 text-right">NC</th>
        <th className="py-1.5 px-2 text-right">% NC</th>
      </tr></thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={r.key + i} className="border-b">
            <td className="py-1.5 px-2 font-medium">{r.key}</td>
            <td className="py-1.5 px-2 text-right font-mono">{r.total}</td>
            <td className="py-1.5 px-2 text-right font-mono">{r.nc}</td>
            <td className="py-1.5 px-2 text-right">
              <Badge className={`text-[10px] border ${r.nc_rate === 0 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : r.nc_rate < 5 ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                {r.nc_rate}%
              </Badge>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function SpcChart({ f }) {
  const data = f.points.map((p, i) => ({ i: i + 1, value: p.value, date: (p.date || '').slice(0, 10) }));
  const cpkTone = f.cpk == null ? 'bg-slate-100 text-slate-700' : f.cpk >= 1.33 ? 'bg-emerald-100 text-emerald-800 border-emerald-300' : f.cpk >= 1.0 ? 'bg-amber-100 text-amber-800 border-amber-300' : 'bg-red-100 text-red-800 border-red-300';
  return (
    <div className="border rounded p-2">
      <div className="flex items-center justify-between mb-1">
        <p className="text-xs font-semibold truncate flex-1">{f.feature}</p>
        <div className="flex items-center gap-1">
          {f.out_of_spec > 0 && <Badge className="bg-red-50 text-red-700 border border-red-200 text-[9px]">{f.out_of_spec} fuori tol.</Badge>}
          <Badge className={`text-[9px] border ${cpkTone}`}>Cpk {f.cpk ?? 'n/d'}</Badge>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={120}>
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="i" tick={{ fontSize: 9 }} />
          <YAxis tick={{ fontSize: 9 }} domain={[Math.min(f.tmin, f.lcl) - 0.01, Math.max(f.tmax, f.ucl) + 0.01]} />
          <Tooltip contentStyle={{ fontSize: 10 }} />
          <ReferenceLine y={f.tmin} stroke="#ef4444" strokeDasharray="4 2" label={{ value: 'LSL', fontSize: 8, position: 'left' }} />
          <ReferenceLine y={f.tmax} stroke="#ef4444" strokeDasharray="4 2" label={{ value: 'USL', fontSize: 8, position: 'left' }} />
          <ReferenceLine y={f.nominal} stroke="#3b82f6" strokeDasharray="2 2" />
          <Line type="monotone" dataKey="value" stroke="#1e293b" strokeWidth={1.5} dot={{ r: 2 }} />
        </LineChart>
      </ResponsiveContainer>
      <div className="text-[9px] text-slate-500 mt-1 grid grid-cols-4 gap-1">
        <span>N: {f.n}</span>
        <span>μ: {f.mean}</span>
        <span>σ: {f.sigma}</span>
        <span>Cp: {f.cp ?? 'n/d'}</span>
      </div>
    </div>
  );
}

// ───── TAB C · Cliente/Audit ─────
function ClientePanel({ filters, filterValue, onSelectCustomer }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!filterValue.customer_code) { setData(null); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('customer_code', filterValue.customer_code);
      if (filterValue.date_from) params.append('date_from', filterValue.date_from);
      if (filterValue.date_to) params.append('date_to', filterValue.date_to);
      const r = await api.get(`/qms/dashboard/cliente?${params.toString()}`);
      setData(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore caricamento cliente');
    } finally { setLoading(false); }
  }, [filterValue.customer_code, filterValue.date_from, filterValue.date_to]);

  useEffect(() => { load(); }, [load]);

  if (!filterValue.customer_code) {
    return (
      <Card><CardContent className="p-6 text-center space-y-3">
        <Building2 className="w-10 h-10 mx-auto text-slate-300" />
        <p className="text-sm text-slate-600">Seleziona un cliente per vedere il report dedicato.</p>
        <div className="max-w-xs mx-auto">
          <Select value={filterValue.customer_code || ''} onValueChange={onSelectCustomer}>
            <SelectTrigger data-testid="cliente-picker"><SelectValue placeholder="Seleziona cliente…" /></SelectTrigger>
            <SelectContent>
              {(filters?.customers || []).map(c => <SelectItem key={c.code} value={c.code}>{c.code} · {c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </CardContent></Card>
    );
  }
  if (loading || !data) return <div className="text-center text-slate-400 py-12">Caricamento…</div>;

  return (
    <div className="space-y-4">
      <Card><CardContent className="p-3">
        <div className="flex items-center gap-3">
          <Building2 className="w-6 h-6 text-slate-500" />
          <div>
            <p className="text-lg font-bold">{data.customer.name}</p>
            <p className="text-[10px] text-slate-500">Codice: {data.customer.code}{data.customer.vat ? ` · P.IVA ${data.customer.vat}` : ''}</p>
          </div>
        </div>
      </CardContent></Card>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <KpiCard testid="cl-kpi-total" label="Controlli periodo" value={fmtInt(data.kpi_period.total)} icon={Activity} sub={`${data.period.from} → ${data.period.to}`} />
        <KpiCard testid="cl-kpi-conf" label="Conformità periodo" value={data.kpi_period.conformity_rate} unit="%" icon={Target}
          tone={data.kpi_period.conformity_rate >= 95 ? 'good' : data.kpi_period.conformity_rate >= 90 ? 'warn' : 'bad'} />
        <KpiCard testid="cl-kpi-derogate" label="Accett. con deroga" value={fmtInt(data.kpi_period.derogate)} icon={Target}
          tone={data.kpi_period.derogate === 0 ? 'neutral' : 'warn'} sub="nel periodo" />
        <KpiCard testid="cl-kpi-ytd-ppm" label="PPM YTD" value={fmtInt(data.kpi_ytd.ppm)} icon={AlertTriangle}
          tone={data.kpi_ytd.ppm < 20000 ? 'good' : data.kpi_ytd.ppm < 50000 ? 'warn' : 'bad'} sub={`dal ${data.period.ytd_from}`} />
        <KpiCard testid="cl-kpi-rqp" label="RQP aperte" value={data.rqp_aperte.count} icon={ClipboardList}
          tone={data.rqp_aperte.count === 0 ? 'good' : 'warn'} />
      </div>

      <Card><CardHeader className="pb-1"><CardTitle className="text-sm">Timeline controlli YTD</CardTitle></CardHeader>
        <CardContent className="p-3">
          {!data.timeline.length ? <p className="text-slate-400 text-sm text-center py-6">Nessun controllo YTD.</p> : (
            <ResponsiveContainer width="100%" height={200}>
              <ComposedChart data={data.timeline}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 9 }} />
                <YAxis yAxisId="left" tick={{ fontSize: 10 }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} domain={[0, 100]} unit="%" />
                <Tooltip contentStyle={{ fontSize: 11 }} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Bar yAxisId="left" dataKey="conformi" name="Conf." fill="#10b981" stackId="a" />
                <Bar yAxisId="left" dataKey="derogate" name="Deroga" fill="#f59e0b" stackId="a" />
                <Bar yAxisId="left" dataKey="non_conformi" name="NC" fill="#ef4444" stackId="a" />
                <Line yAxisId="right" type="monotone" dataKey="conformity_rate" stroke="#3b82f6" strokeWidth={2} dot={{ r: 2 }} name="% Conf." />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card><CardHeader className="pb-1"><CardTitle className="text-sm flex items-center gap-2"><ArrowDownRight className="w-4 h-4 text-red-500" />Non conformità nel periodo ({data.nc_list.length})</CardTitle></CardHeader>
          <CardContent className="p-0 max-h-96 overflow-y-auto">
            {!data.nc_list.length ? <p className="text-slate-400 text-sm text-center py-6">Nessuna NC nel periodo.</p> : (
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-white"><tr className="border-b text-left text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="py-1.5 px-2">Data</th><th className="py-1.5 px-2">Prod.</th><th className="py-1.5 px-2">Lotto</th><th className="py-1.5 px-2">Esito</th><th className="py-1.5 px-2">Operatore</th>
                </tr></thead>
                <tbody>
                  {data.nc_list.map(r => (
                    <tr key={r.id} className="border-b hover:bg-slate-50">
                      <td className="py-1.5 px-2 font-mono text-[10px]">{(r.data_ora_controllo || '').slice(0, 10)}</td>
                      <td className="py-1.5 px-2">{r.product_code} <span className="text-slate-400">{r.revision}</span></td>
                      <td className="py-1.5 px-2">{r.lotto || '—'}</td>
                      <td className="py-1.5 px-2"><Badge className="text-[9px] border" style={{ backgroundColor: (ESITO_COLORS[r.esito_controllo] || '#94a3b8') + '20', color: ESITO_COLORS[r.esito_controllo] || '#94a3b8' }}>{r.esito_controllo}</Badge></td>
                      <td className="py-1.5 px-2 text-[10px]">{r.controllore || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        <Card><CardHeader className="pb-1"><CardTitle className="text-sm">SPC-Lite YTD · Quote critiche</CardTitle></CardHeader>
          <CardContent className="p-3 max-h-96 overflow-y-auto">
            {!data.spc_lite.length ? <p className="text-slate-400 text-sm text-center py-6">Dati insufficienti per SPC.</p> : (
              <div className="space-y-3">{data.spc_lite.map(f => <SpcChart key={f.feature} f={f} />)}</div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ───── Main Page ─────
export default function QmsDashboardPage() {
  const [filters, setFilters] = useState(null);
  const [filterValue, setFilterValue] = useState(() => {
    const to = new Date();
    const from = new Date(); from.setDate(from.getDate() - 30);
    return {
      date_from: from.toISOString().slice(0, 10),
      date_to: to.toISOString().slice(0, 10),
      customer_code: '', product_code: '', fase: '', causale: '',
    };
  });
  const [direzionaleData, setDirezionaleData] = useState(null);
  const [operativaData, setOperativaData] = useState(null);
  const [tab, setTab] = useState('direzionale');

  const loadFilters = useCallback(async () => {
    try {
      const r = await api.get('/qms/dashboard/filters');
      setFilters(r.data);
    } catch { /* silent */ }
  }, []);

  const buildQuery = useCallback(() => {
    const p = new URLSearchParams();
    if (filterValue.date_from) p.append('date_from', filterValue.date_from);
    if (filterValue.date_to) p.append('date_to', filterValue.date_to);
    if (filterValue.customer_code) p.append('customer_code', filterValue.customer_code);
    if (filterValue.product_code) p.append('product_code', filterValue.product_code);
    if (filterValue.fase) p.append('fase', filterValue.fase);
    if (filterValue.causale) p.append('causale', filterValue.causale);
    return p.toString();
  }, [filterValue]);

  const load = useCallback(async () => {
    try {
      const q = buildQuery();
      const [d, o] = await Promise.all([
        api.get(`/qms/dashboard/direzionale?${q}`),
        api.get(`/qms/dashboard/operativa?${q}`),
      ]);
      setDirezionaleData(d.data);
      setOperativaData(o.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore caricamento dashboard');
    }
  }, [buildQuery]);

  useEffect(() => { loadFilters(); }, [loadFilters]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 space-y-4" data-testid="qms-dashboard-page">
      <div className="flex items-center gap-2">
        <LayoutDashboard className="w-6 h-6" />
        <h1 className="text-2xl font-bold">Dashboard QMS · Registro Misure</h1>
      </div>

      <FiltersBar value={filterValue} setValue={setFilterValue} filters={filters} onRefresh={load} hideCustomer={tab === 'cliente'} />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="direzionale" data-testid="tab-direzionale"><Target className="w-4 h-4 mr-1" />Direzionale</TabsTrigger>
          <TabsTrigger value="operativa" data-testid="tab-operativa"><Activity className="w-4 h-4 mr-1" />Operativa</TabsTrigger>
          <TabsTrigger value="cliente" data-testid="tab-cliente"><Users className="w-4 h-4 mr-1" />Cliente/Audit</TabsTrigger>
        </TabsList>
        <TabsContent value="direzionale"><DirezionalePanel data={direzionaleData} /></TabsContent>
        <TabsContent value="operativa"><OperativaPanel data={operativaData} /></TabsContent>
        <TabsContent value="cliente">
          <ClientePanel filters={filters} filterValue={filterValue} onSelectCustomer={(v) => setFilterValue({ ...filterValue, customer_code: v })} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
